/**
 * ClozeSlot — the atomic unit of the graduated cloze scaffold.
 *
 * A single uniformly-sized pill that either displays a revealed letter
 * (bold, centered) or stands as an empty box (transparent contents,
 * faint chrome). Pills across a word are identical in width and height
 * so the row reads as an evenly-spaced grid, Wordle-style.
 *
 * Implementation note: styles are inlined rather than read from a stylesheet
 * or Tailwind utilities. We hit a real bug where the per-slot rules were
 * sometimes missing in dev (Tailwind JIT didn't pick up arbitrary-value
 * classes in newly-added files, and stale custom CSS made blank pills
 * narrower than letter pills). Inline styles can't go stale or fail to
 * compile — every slot is guaranteed identical.
 *
 * Sizing is fixed (width 1.5em, height 1.7em) so a row of slots always
 * lays out identically regardless of which letters are visible — empty
 * slots can't be narrower than letter slots. Letters wider than 1.5em
 * (bold "m", "w") will visually overflow rather than expand the pill;
 * that's an acceptable trade for uniform layout.
 *
 * Props:
 *   char     — single character to display when revealed; ignored otherwise
 *   revealed — boolean; true = show the letter, false = empty pill
 */
const BASE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  width: '1.5em',
  height: '1.7em',
  borderRadius: '0.25em',
  lineHeight: 1,
  boxSizing: 'border-box',
  border: '1px solid rgba(96, 165, 250, 0.5)',
  padding: 0,
  margin: 0,
  textAlign: 'center',
  // CRITICAL: cloze pills sit inside <p>s that have `text-indent: 2em`
  // for prose typography. text-indent is inherited and applies to the
  // first line of inline content in any descendant block formatting
  // context — including the letter inside this inline-flex pill, which
  // would otherwise get shifted 2em to the right (out of the pill).
  textIndent: 0,
};

const REVEALED_STYLE = {
  ...BASE_STYLE,
  fontWeight: 700,
  background: 'rgba(59, 130, 246, 0.12)',
};

const BLANK_STYLE = {
  ...BASE_STYLE,
  background: 'rgba(59, 130, 246, 0.04)',
  color: 'transparent',
};

export default function ClozeSlot({ char, revealed }) {
  if (revealed) {
    return <span style={REVEALED_STYLE}>{char}</span>;
  }
  return (
    <span style={BLANK_STYLE} aria-hidden="true">
      {' '}
    </span>
  );
}
